import {
  ActivityPurchaseModel,
  ActivityPurchaseMutationInput,
} from 'api-hooks/order/activity';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState, Input } from 'components/elements';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useRevertVoidAction from 'hooks/use-revert-void-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import OtherPriceComponent from 'legacy-modules/order/component/other-price-component';
import ParentOrderRelatedTable from 'legacy-modules/order/component/parent-children-purchase-table';
import SaleRelatedTable from 'legacy-modules/order/component/sale-related-table';
import SalePurchaseRefundTable from 'legacy-modules/refund/components/sale-purchase-table';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import ActivityDetailSection from './activity-details';
import {
  ActivityPurchaseFormSchema,
  ActivityPurchaseFormType,
} from './form-type';
import PurchasesDetailCard from './purchase-detail-card';
import ActivityPurchaseSelectInput from './select-input';
import SummaryActivityPurchase from './summary';
import totalCalculation from './total-calculation';
import useGetDefaultValues from '../hooks/use-get-default-values';
import { FormContent, FormDivider } from '../style';

interface Props {
  activityPurchases?: ActivityPurchaseModel;
  onSubmit: (
    input: ActivityPurchaseMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ActivityPurchaseModel | undefined>;
  renderError?: React.ReactNode;
  activitySale?: ActivitySaleModel;
  defaultValues?: ActivityPurchaseFormType;
}

export default function ActivityPurchaseForm(props: Props) {
  const { t } = useTranslation();
  const { activityPurchases, renderError, activitySale } = props;
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues({
    activityPurchase: activityPurchases,
    activitySale,
    defaultValues: props.defaultValues,
  });

  const resolver = useYupValidationResolver(ActivityPurchaseFormSchema());

  const methods = useForm<ActivityPurchaseFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: ActivityPurchaseFormType) => {
      const {
        activitySale,
        tempTransactionAt,
        agentStatus,
        remainingTransactionQuota,
        isExceedingQuota,
        //remove all unused data
        activityPurchases,
        ...rest
      } = values;
      try {
        const total = totalCalculation({
          activityPurchases: values.activityPurchases,
          discount: values.discount,
          otherCost: values.otherCost,
        });

        //regenerate activityPurchases
        const ap = activityPurchases.map((as) => ({
          ...as,
          activityPurchaseGuests: as.activityPurchaseGuests.map((asg, idx) => {
            const defaultAsg = {
              ...asg,
            };

            if (as.isGroupedPrice && idx > 0) {
              defaultAsg.activityPurchaseGuestDetails =
                as.activityPurchaseGuests[0].activityPurchaseGuestDetails;
            }

            return defaultAsg;
          }),
        }));

        const needApproval =
          rest.transactionAt?.getTime() !== tempTransactionAt?.getTime();

        const result = {
          ...rest,
          total,
          needApproval,
          activityPurchases: ap,
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const permission = useGetCrudPermission('activity-purchases');
  const { onVoid, isActive, voidMutate, voidPermission } = useVoidAction(
    'activity-purchases',
    activityPurchases,
  );
  const { revertDraftButton } = useRevertDraftAction(
    'activity-purchases',
    activityPurchases,
  );
  const { revertVoidButton } = useRevertVoidAction(
    'activity-purchases',
    activityPurchases,
  );
  const { resolverButton } = useResolveApproval(
    'activity-purchases',
    activityPurchases,
  );
  const { requestApprovalButton } = useApprovalAction(
    'activity-purchases',
    activityPurchases,
  );

  const onRenderRightComponent = React.useMemo(() => {
    if (activityPurchases) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
          {revertVoidButton}
        </div>
      );
    }
    return undefined;
  }, [
    activityPurchases,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
    revertVoidButton,
  ]);

  const onRelatedOrderAfterChange = React.useCallback(
    (value: ActivityPurchaseModel) => {
      methods.setValue('parentRefId', value.refId);
      methods.setValue('agentId', value.agent?.id || '');
      methods.setValue('invoiceId', value.sale?.id || '');
      methods.setValue('tourId', value.tour?.id || '');
      methods.setValue('type', value.type || 'sales');
      methods.setValue('paymentChannelId', value.paymentChannel?.id || '');
    },
    [methods],
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!activityPurchases}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={
        activityPurchases &&
        `${t('common:others_purchase')} - ${activityPurchases.number}`
      }
      keyName={asPath}
    >
      <FormContent>
        {renderError}
        <FormState>
          {({ editable }) => (
            <FormHeader
              data={activityPurchases}
              noSubmit
              noCancel
              onDelete={onVoid}
              noDelete={editable || !voidPermission}
              deleteText={t('common:void')}
              title={
                activityPurchases ? (
                  <TitleStatus
                    type="purchase"
                    title={`${t('common:others_purchase')} - ${
                      activityPurchases.number
                    }`}
                    status={activityPurchases.status}
                  />
                ) : (
                  t('common:add_extra', {
                    extra: t('common:others_purchase'),
                  })
                )
              }
              isDeleteLoading={voidMutate.isLoading}
              noEdit={!isActive || !permission.create}
              onRenderRightComponent={onRenderRightComponent}
            />
          )}
        </FormState>
        <div>
          {activityPurchases &&
            !!(activityPurchases?.releasePurchases || []).length && (
              <SalePurchaseRefundTable
                data={activityPurchases.releasePurchases}
              />
            )}
          {activityPurchases && (
            <>
              <SaleRelatedTable
                data={activityPurchases.sale}
                navigation={{
                  sale: 'ActivitySalesView',
                  purchase: 'ActivityPurchasesView',
                }}
              />
              <SeparatorVertical $margin={24} />
            </>
          )}
          {activityPurchases &&
            (!!activityPurchases.parentPurchase ||
              !!(activityPurchases.childrenPurchases || []).length) && (
              <ParentOrderRelatedTable
                data={activityPurchases}
                navigation="ActivityPurchasesView"
              />
            )}
          <SectionContainer>
            <HalfContainer>
              <ActivityPurchaseSelectInput
                label={t('common:related_order')}
                placeholder={t('common:choose_order')}
                name="parentRefId"
                onAfterDetailChange={onRelatedOrderAfterChange}
              />
            </HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('ticketSales:group_code')}
                placeholder={t('ticketSales:group_code')}
              />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
          </SectionContainer>
          <SeparatorVertical $margin={16} />
          <PurchasesDetailCard activityPurchase={activityPurchases} />
          <SeparatorVertical $margin={24}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <ActivityDetailSection />
          <SeparatorVertical $margin={16}>
            <div style={{ height: 2 }} />
          </SeparatorVertical>
          <OtherPriceComponent
            type="activity-purchases"
            data={props.activityPurchases}
          />
          <SeparatorVertical $margin={32} />
          <FormDivider />
        </div>
      </FormContent>
      <SummaryActivityPurchase activityPurchases={activityPurchases} />
    </Form>
  );
}
